
import { useResumeState } from "./useResumeState";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useCredits } from "@/hooks/useCredits";
import { useQueryClient } from "@tanstack/react-query";

/**
 * Hook for managing resume payment operations
 */
export const useResumePayment = () => {
  const { selectedResume, setShowPayment, setShowFormatDialog, setIsProcessing, isProcessing } = useResumeState();
  const { toast } = useToast();
  const { credits, updateCredits } = useCredits();
  const queryClient = useQueryClient();

  const handleDownloadClick = (resume: any) => {
    if (!resume.is_paid) {
      setShowPayment(true);
    } else {
      setShowFormatDialog(true);
    }
  };

  const handlePayment = async (useCredit: boolean = false) => {
    if (!selectedResume || !selectedResume.user_id) return;
    
    setIsProcessing(true);
    try {
      if (useCredit) {
        if (credits <= 0) {
          throw new Error("No credits available");
        }

        // Record credit usage transaction
        const { error: transactionError } = await supabase
          .from('credit_transactions')
          .insert({
            user_id: selectedResume.user_id,
            type: 'usage',
            amount: 1,
            description: `Used for resume: ${selectedResume.title}`
          });

        if (transactionError) throw transactionError;

        // Update user credits
        await updateCredits.mutateAsync(credits - 1);
      }

      // Mark resume as paid
      const { error: updateError } = await supabase
        .from('resumes')
        .update({ is_paid: true })
        .eq('id', selectedResume.id);

      if (updateError) throw updateError;

      // Invalidate queries to update UI
      queryClient.invalidateQueries({ queryKey: ["resumes"] });
      setShowPayment(false);
      setShowFormatDialog(true);
      
      toast({
        title: useCredit ? "Credit Used" : "Payment Successful",
        description: "You can now download your optimized resume!",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    handleDownloadClick,
    handlePayment,
    isProcessing
  };
};
